import React, { useRef, useState } from 'react'
import * as Yup from 'yup'
import { FormikProps } from 'formik'
import { Text, Box, Flex, Input } from '@sketch/components'
import {
  Separator,
  DescriptionTextArea,
  ScrollingContainer,
  PaddedContainer,
  Field,
} from './FormEditCreate.styles'
import { EditImage } from './EditImage'
import { LibrariesPanel } from './LibrariesPanel'
import { ModalInjectedProps } from 'containers/ModalManager'
import { Library, FormProps, DesignSystem } from '../../types'
import { useGetEligibleSharesQuery } from '@sketch/gql-types'
import { useCalculateScrollableAreaHeight } from './useCalculateScrollableAreaHeight'

interface FormEditCreateProps extends ModalInjectedProps {
  designSystemToEdit?: DesignSystem
  onImageChange: (file: File) => void
  formikbag: FormikProps<FormProps>
  submitting: boolean
  workspaceId: string
  logo?: string | null
}

// Form Validation
export const validationSchema = Yup.object().shape({
  name: Yup.string().required(
    `Give your System a name. Don't worry, you can change it later.`
  ),
  libraries: Yup.array().required(
    `Please select some libraries to add to your System.`
  ),
})

export const FormEditCreate = (props: FormEditCreateProps) => {
  const { onImageChange, formikbag, submitting, workspaceId, logo } = props
  const [searchLibraries, setSearchLibraries] = useState('')
  const fixedHeightRef = useRef<HTMLDivElement>(null)
  const listHeight = useRef<HTMLDivElement>(null)

  const { data, loading, error } = useGetEligibleSharesQuery({
    variables: {
      identifier: workspaceId ?? '',
      search: searchLibraries,
      limit: 20,
    },
    skip: !workspaceId,
  })

  const libraries = data?.designSystemEligibleShares?.entries

  const scrollablePanelHeight = useCalculateScrollableAreaHeight({
    fixedHeightRef,
    hasLibraries: !!libraries,
    listHeight,
  })

  return (
    <>
      <PaddedContainer ref={fixedHeightRef}>
        <Flex mb={5}>
          <Box mr={4}>
            <EditImage onChange={onImageChange} logo={logo} />
          </Box>
          <Box width="100%">
            <label htmlFor="name">Name</label>
            <Box mt={1}>
              <Field
                name="text"
                errorText={
                  formikbag.touched.name ? formikbag.errors.name : undefined
                }
              >
                <Input
                  name="name"
                  type="text"
                  value={formikbag.values.name}
                  placeholder="Give your design system a name"
                  disabled={submitting}
                  onChange={formikbag.handleChange}
                />
              </Field>
              <Text textStyle="copy.quaternary.standard.C">
                PNG or JPG up to 2 MB
              </Text>
            </Box>
          </Box>
        </Flex>
      </PaddedContainer>
      <ScrollingContainer ref={listHeight} height={scrollablePanelHeight}>
        <label htmlFor="description">Description (Optional)</label>
        <Box mt={1}>
          <DescriptionTextArea
            name="description"
            value={formikbag.values.description}
            onChange={formikbag.handleChange}
            placeholder="This design system is for…"
            rows={10}
            disabled={submitting}
          />
        </Box>
        <Separator />
        <LibrariesPanel
          libraries={(libraries as Library[]) ?? []}
          search={searchLibraries}
          handleSearchUpdate={setSearchLibraries}
          loading={loading}
          formikbag={formikbag}
          onChange={formikbag.handleChange}
          errorLoad={!!error}
        />
      </ScrollingContainer>
    </>
  )
}
